// stores/auth.js
import { defineStore } from "pinia";
import { ref, computed } from "vue";
import { login as apiLogin } from "../api/axios";
import { jwtDecode } from "jwt-decode";
import TooManyRequestsError from "@/utils/TooManyRequestsError.ts";
import * as Sentry from "@sentry/vue";

export class NoOrganizationAssignedError extends Error {
  constructor() {
    super("No organization assigned to the user.");
    this.name = "NoOrganizationAssignedError";
  }
}

export class AuthenticationError extends Error {
  constructor() {
    super("Authentication failed. Please check your credentials.");
    this.name = "AuthenticationError";
  }
}

export const useAuthStore = defineStore("auth", () => {
  // State
  const token = ref(localStorage.getItem("token"));

  // Getters
  const isAuthenticated = computed(() => !!token.value);
  const roles = computed(() => {
    if (!token.value) return [];
    const decoded: any = jwtDecode(token.value);
    const roles = decoded["http://schemas.microsoft.com/ws/2008/06/identity/claims/role"];
    if (!roles) {
      return [];
    }
    return Array.isArray(roles) ? roles : [roles];
  });
  const isAdmin = computed(() => {
    return roles.value.includes("admin");
  });
  const username = computed(() => {
    if (!token.value) return null;
    const decoded: any = jwtDecode(token.value);
    return decoded["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress"];
  });
  const userId = computed(() => {
    if (!token.value) return null;
    const decoded: any = jwtDecode(token.value);
    return decoded["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier"];
  });

  // Actions
  const setToken = (newToken: string) => {
    token.value = newToken;
    localStorage.setItem("token", newToken);
  };

  const logout = () => {
    token.value = null;
    localStorage.removeItem("token");
  };

  const login = async (username: string, password: string) => {
    try {
      const token = await apiLogin(username, password);
      setToken(token);
      Sentry.setUser({
        email: username,
        id: userId.value || undefined,
      });
      return;
    } catch (error: any) {
      console.error("Login failed:", error);
      if (
        error.response &&
        error.response.status === 401 &&
        error.response.data &&
        error.response.data.errorCode === 2
      ) {
        throw new NoOrganizationAssignedError();
      } else {
        if (error instanceof TooManyRequestsError) {
          throw error; // rethrow TooManyRequestsError without wrapping
        } else {
          throw new AuthenticationError();
        }
      }
    }
  };

  const setSentryUser = () => {
    if (isAuthenticated.value) {
      Sentry.setUser({
        email: username.value || undefined,
        id: userId.value || undefined,
      });
    } else {
      Sentry.setUser(null);
    }
  };

  // called once when store is created (page reload)
  setSentryUser();

  return {
    token,
    username,
    isAuthenticated,
    isAdmin,
    logout,
    login,
  };
});
